<script setup>
import { ref, computed, watch } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'
import PdfButton from '@/components/PdfButton.vue'

const props = defineProps({
  equipement:    Object,
  equipements:   Array,
  debut:         String,
  fin:           String,
  sommes:        Object,
  data:          Array,
  can_see_costs: Boolean,
  xlsx_url:      String,
  pdf_url:       String,
})

const selectedId = ref(props.equipement.id)
const dateDebut  = ref(props.debut)
const dateFin    = ref(props.fin)

const pageSize    = ref(25)
const currentPage = ref(1)
watch(pageSize, () => { currentPage.value = 1 })

const totalPages  = computed(() => Math.max(1, Math.ceil(props.data.length / pageSize.value)))
const paginated   = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return props.data.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) pages.push(i)
  return pages
})

function rechercher() {
  const params = new URLSearchParams({ id: selectedId.value, debut: dateDebut.value, fin: dateFin.value })
  window.location.href = `/compilations/equipement?${params}`
}

function fmtCurrency(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(val)
}
function fmtQty(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="equipement.show_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">Compilation — {{ equipement.nom }}</span>
      </div>
    </template>

    <template #topbar-right>
      <a v-if="data.length" :href="xlsx_url"
         class="px-4 py-2.5 text-xs font-black uppercase tracking-widest rounded-md text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all flex items-center gap-1.5">
        <span class="material-symbols-outlined text-sm">download</span>XLSX
      </a>
      <PdfButton v-if="data.length" :url="pdf_url" />
    </template>

    <div class="p-8 space-y-6">

      <!-- Info + Sommaire -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <!-- Détails équipement -->
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">{{ equipement.code }} — {{ equipement.nom }}</h2>
            <a :href="equipement.edit_url"
               class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
          </div>
          <table class="w-full text-left border-collapse">
            <tbody class="divide-y divide-outline-variant/10">
              <tr class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Code</th>
                <td class="px-6 py-3 font-mono text-sm font-bold text-primary">{{ equipement.code }}</td>
              </tr>
              <tr class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Nom</th>
                <td class="px-6 py-3 text-sm text-on-surface">{{ equipement.nom }}</td>
              </tr>
              <tr v-if="equipement.type_equipement" class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Type</th>
                <td class="px-6 py-3 text-sm text-on-surface">{{ equipement.type_equipement }}</td>
              </tr>
              <tr v-if="can_see_costs" class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Taux horaire</th>
                <td class="px-6 py-3 font-mono text-sm font-bold text-on-surface">{{ fmtCurrency(equipement.taux_horaire) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Sommaire -->
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Sommaire</h2>
          </div>
          <table class="w-full text-left border-collapse">
            <tbody class="divide-y divide-outline-variant/10">
              <tr class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Début</th>
                <td class="px-6 py-3 font-mono text-sm text-on-surface">{{ debut }}</td>
              </tr>
              <tr class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Fin</th>
                <td class="px-6 py-3 font-mono text-sm text-on-surface">{{ fin }}</td>
              </tr>
              <tr class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Temps</th>
                <td class="px-6 py-3 font-mono text-sm font-bold text-on-surface">{{ fmtQty(sommes.temps) }}</td>
              </tr>
              <tr v-if="can_see_costs" class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Coûts</th>
                <td class="px-6 py-3 font-mono text-sm font-bold text-primary">{{ fmtCurrency(sommes.couts) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Filtre -->
      <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
        <div class="px-6 py-4 flex items-end gap-4 flex-wrap">
          <div class="flex-1 min-w-[200px]">
            <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Équipement</label>
            <select v-model="selectedId"
                    class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option v-for="e in equipements" :key="e.id" :value="e.id">{{ e.nom_complet }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Début</label>
            <input v-model="dateDebut" type="date"
                   class="bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20" />
          </div>
          <div>
            <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Fin</label>
            <input v-model="dateFin" type="date"
                   class="bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20" />
          </div>
          <button @click="rechercher"
                  class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg">
            <span class="material-symbols-outlined text-sm">search</span>Rechercher
          </button>
        </div>
      </div>

      <!-- Tableau -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
        <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">
            Rapports compilés du {{ debut }} au {{ fin }} ({{ data.length }})
          </h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-sm">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Date</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Type</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Projet</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Estimation</th>
                <th v-if="can_see_costs" class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right">Taux</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right">Quantité</th>
                <th v-if="can_see_costs" class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right">Coûts</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-if="!data.length">
                <td :colspan="can_see_costs ? 7 : 5" class="px-6 py-12 text-center text-outline text-sm">Aucune donnée pour cette période</td>
              </tr>
              <tr v-for="(d, i) in paginated" :key="i"
                  :class="['hover:bg-surface-container-highest/20 transition-colors', i % 2 === 1 ? 'bg-surface-container-low/20' : '']">
                <td class="px-6 py-3 font-mono text-xs whitespace-nowrap">
                  <a :href="d.rapport_url" class="text-primary hover:brightness-125 transition-colors">{{ d.date }}</a>
                </td>
                <td class="px-6 py-3 text-xs text-outline">{{ d.type }}</td>
                <td class="px-6 py-3">
                  <a :href="d.projet_url" class="font-mono font-bold text-primary text-xs hover:brightness-125 transition-colors">{{ d.projet_code }}</a>
                </td>
                <td class="px-6 py-3">
                  <a :href="d.estimation_url" class="font-mono font-bold text-primary text-xs hover:brightness-125 transition-colors">{{ d.estimation_code }}</a>
                </td>
                <td v-if="can_see_costs" class="px-6 py-3 text-right font-mono text-xs text-on-surface">{{ fmtCurrency(d.taux_horaire) }}</td>
                <td class="px-6 py-3 text-right font-mono text-xs text-on-surface">{{ fmtQty(d.quantite) }}</td>
                <td v-if="can_see_costs" class="px-6 py-3 text-right font-mono text-xs text-on-surface">{{ fmtCurrency(d.couts) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Pagination footer -->
        <div v-if="data.length" class="px-6 py-4 bg-surface-container-high/40 border-t border-outline-variant/15 flex justify-between items-center">
          <div class="text-[10px] text-outline font-black uppercase tracking-widest">
            Affichage de {{ (currentPage - 1) * pageSize + 1 }}
            à {{ Math.min(currentPage * pageSize, data.length) }}
            sur {{ data.length }} entrée{{ data.length > 1 ? 's' : '' }}
          </div>
          <div class="flex gap-1">
            <button
              :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage--"
            >
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <template v-for="p in pageNumbers" :key="p">
              <button
                v-if="p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1"
                :class="[
                  'w-8 h-8 flex items-center justify-center rounded text-[10px] font-black transition-colors',
                  p === currentPage
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container-low border border-outline-variant/10 text-outline hover:text-primary'
                ]"
                @click="currentPage = p"
              >{{ p }}</button>
              <span
                v-else-if="p === 2 || p === totalPages - 1"
                class="w-8 h-8 flex items-center justify-center text-outline text-sm"
              >…</span>
            </template>
            <button
              :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage++"
            >
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  </AppLayout>
</template>
